import React, { useState, useEffect } from "react";
import { getAllLookups, createLookup, updateLookup, deleteLookup } from "../../../api/t2tVendorLookupApi";
import { useToast, useConfirm } from "../Layout/T2TUIProvider";

const TYPES = [
  { value: "company_code",   label: "Company Codes" },
  { value: "plant_code",     label: "Plant Codes" },
  { value: "payment_term",   label: "Payment Terms" },
  { value: "delivery_term",  label: "Delivery Terms" },
  { value: "account_group",  label: "Account Groups" },
  { value: "currency",       label: "Currencies" },
];

// BUG-17: per-type placeholders + helper text so the modal isn't always
// showing company-code examples (e.g. "LTFL / LT Foods Limited").
const TYPE_HINTS = {
  company_code:  { codeHint: "e.g. 5000",  labelHint: "e.g. LT Foods Ltd India" },
  plant_code:    { codeHint: "e.g. 5001",  labelHint: "e.g. LTF - Bahalgarh" },
  payment_term:  { codeHint: "e.g. Y016",  labelHint: "e.g. Within 30 days Due net" },
  delivery_term: { codeHint: "e.g. FOB",   labelHint: "e.g. Free on Board" },
  account_group: { codeHint: "e.g. Z101",  labelHint: "e.g. Domestic — Regular" },
  currency:      { codeHint: "e.g. INR",   labelHint: "e.g. Indian Rupee" },
};

// BUG-17: Country → currency mapping. Frontend-only convenience for the
// Currencies tab — the country itself is NOT stored, just the code/label/symbol
// it auto-fills. Sorted A→Z, then "Other (manual)" sentinel for non-listed.
const COUNTRY_TO_CURRENCY = [
  // [country, code, name, symbol]
  ["Australia",        "AUD", "Australian Dollar",   "A$"],
  ["Bahrain",          "BHD", "Bahraini Dinar",      ".د.ب"],
  ["Bangladesh",       "BDT", "Bangladeshi Taka",    "৳"],
  ["Belgium",          "EUR", "Euro",                "€"],
  ["Bhutan",           "BTN", "Bhutanese Ngultrum",  "Nu."],
  ["Brazil",           "BRL", "Brazilian Real",      "R$"],
  ["Canada",           "CAD", "Canadian Dollar",     "C$"],
  ["China",            "CNY", "Chinese Yuan",        "¥"],
  ["Denmark",          "DKK", "Danish Krone",        "kr"],
  ["Egypt",            "EGP", "Egyptian Pound",      "£"],
  ["Finland",          "EUR", "Euro",                "€"],
  ["France",           "EUR", "Euro",                "€"],
  ["Germany",          "EUR", "Euro",                "€"],
  ["Greece",           "EUR", "Euro",                "€"],
  ["India",            "INR", "Indian Rupee",        "₹"],
  ["Indonesia",        "IDR", "Indonesian Rupiah",   "Rp"],
  ["Ireland",          "EUR", "Euro",                "€"],
  ["Israel",           "ILS", "Israeli Shekel",      "₪"],
  ["Italy",            "EUR", "Euro",                "€"],
  ["Japan",            "JPY", "Japanese Yen",        "¥"],
  ["Kuwait",           "KWD", "Kuwaiti Dinar",       "د.ك"],
  ["Malaysia",         "MYR", "Malaysian Ringgit",   "RM"],
  ["Mexico",           "MXN", "Mexican Peso",        "$"],
  ["Nepal",            "NPR", "Nepalese Rupee",      "Rs"],
  ["Netherlands",      "EUR", "Euro",                "€"],
  ["New Zealand",      "NZD", "New Zealand Dollar",  "NZ$"],
  ["Norway",           "NOK", "Norwegian Krone",     "kr"],
  ["Oman",             "OMR", "Omani Rial",          "﷼"],
  ["Portugal",         "EUR", "Euro",                "€"],
  ["Qatar",            "QAR", "Qatari Riyal",        "﷼"],
  ["Russia",           "RUB", "Russian Ruble",       "₽"],
  ["Saudi Arabia",     "SAR", "Saudi Riyal",         "﷼"],
  ["Singapore",        "SGD", "Singapore Dollar",    "S$"],
  ["South Africa",     "ZAR", "South African Rand",  "R"],
  ["South Korea",      "KRW", "South Korean Won",    "₩"],
  ["Spain",            "EUR", "Euro",                "€"],
  ["Sri Lanka",        "LKR", "Sri Lankan Rupee",    "Rs"],
  ["Sweden",           "SEK", "Swedish Krona",       "kr"],
  ["Switzerland",      "CHF", "Swiss Franc",         "CHF"],
  ["Thailand",         "THB", "Thai Baht",           "฿"],
  ["Turkey",           "TRY", "Turkish Lira",        "₺"],
  ["United Arab Emirates", "AED", "UAE Dirham",      "د.إ"],
  ["United Kingdom",   "GBP", "British Pound",       "£"],
  ["United States",    "USD", "US Dollar",           "$"],
  ["Vietnam",          "VND", "Vietnamese Dong",     "₫"],
];

const emptyForm = { lookup_type: "company_code", code: "", label: "", symbol: "", country: "", is_active: 1 };

const LookupAdmin = () => {
  const toast   = useToast();
  const confirm = useConfirm();

  const [activeType, setActiveType] = useState("company_code");
  const [rows, setRows]             = useState([]);
  const [allRows, setAllRows]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [modal, setModal]           = useState(null); // null | { mode: 'add'|'edit', row }
  const [form, setForm]             = useState(emptyForm);
  const [saving, setSaving]         = useState(false);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    const resp = await getAllLookups();
    if (!resp.error) {
      const raw = resp.data?.data;
      // API returns grouped object { company_code: [...], ... } — flatten to array
      const list = Array.isArray(raw)
        ? raw
        : raw && typeof raw === 'object'
          ? Object.values(raw).flat()
          : [];
      setAllRows(list);
      setRows(list.filter((r) => r.lookup_type === activeType));
    }
    setLoading(false);
  };

  useEffect(() => {
    setRows(allRows.filter((r) => r.lookup_type === activeType));
  }, [activeType, allRows]);

  const openAdd = () => {
    setForm({ ...emptyForm, lookup_type: activeType });
    setModal({ mode: "add" });
  };

  const openEdit = (row) => {
    setForm({
      lookup_type: row.lookup_type,
      code:        row.code,
      label:       row.label,
      symbol:      row.symbol || "",
      country:     "",
      is_active:   row.is_active ?? 1,
    });
    setModal({ mode: "edit", row });
  };

  const save = async () => {
    if (!form.code.trim() || !form.label.trim()) { toast("Code and label are required.", "error"); return; }
    setSaving(true);
    // Build payload — include symbol only when type is currency.
    const payload = {
      lookup_type: form.lookup_type,
      code:        form.code,
      label:       form.label,
      is_active:   form.is_active,
      ...(form.lookup_type === "currency" ? { symbol: form.symbol || null } : {}),
    };
    const resp = modal.mode === "add"
      ? await createLookup(payload)
      : await updateLookup(modal.row.lookup_id || modal.row.id, payload);
    setSaving(false);
    if (resp.error) { toast(resp.error.message || "Save failed.", "error"); return; }
    toast(modal.mode === "add" ? "Lookup added." : "Lookup updated.", "success");
    setModal(null);
    load();
  };

  const remove = async (row) => {
    const ok = await confirm(`Delete "${row.code} — ${row.label}"? This cannot be undone.`);
    if (!ok) return;
    const resp = await deleteLookup(row.lookup_id || row.id);
    if (resp.error) { toast(resp.error.message || "Delete failed.", "error"); return; }
    toast("Lookup deleted.", "success");
    load();
  };

  // BUG-17: Country picker for currencies — fills code/label/symbol.
  // Smart auto-fill (BUG-09/11 pattern): only overwrites Code/Label/Symbol when
  // the existing values are empty OR equal what the previous country auto-filled.
  // So if admin manually customised any field, picking a new country won't clobber it.
  const onCountryPick = (countryName) => {
    const entry = COUNTRY_TO_CURRENCY.find((c) => c[0] === countryName);
    if (!entry) {
      // "Other (manual)" or nothing — keep current values, just remember the choice.
      setForm({ ...form, country: countryName });
      return;
    }
    const [, code, label, symbol] = entry;
    // Find what the previously-selected country auto-filled (if any), so we
    // can detect "still pristine" state.
    const prev = form.country
      ? COUNTRY_TO_CURRENCY.find((c) => c[0] === form.country)
      : null;
    const codeIsPristine   = !form.code   || (prev && form.code   === prev[1]);
    const labelIsPristine  = !form.label  || (prev && form.label  === prev[2]);
    const symbolIsPristine = !form.symbol || (prev && form.symbol === prev[3]);
    setForm({
      ...form,
      country: countryName,
      code:    codeIsPristine   ? code   : form.code,
      label:   labelIsPristine  ? label  : form.label,
      symbol:  symbolIsPristine ? symbol : form.symbol,
    });
  };

  const isCurrency = form.lookup_type === "currency";
  const isCurrencyTab = activeType === "currency";

  const codeHint  = TYPE_HINTS[form.lookup_type]?.codeHint  || "Code";
  const labelHint = TYPE_HINTS[form.lookup_type]?.labelHint || "Label";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div className="t2t-tab-group">
          {TYPES.map((t) => (
            <button key={t.value} className={`t2t-tab${activeType === t.value ? " active" : ""}`} onClick={() => setActiveType(t.value)}>
              {t.label}
            </button>
          ))}
        </div>
        <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={openAdd}>+ Add</button>
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", padding: 16 }}>Loading…</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#F8FAFC", borderBottom: "2px solid #E2E8F0" }}>
              {(isCurrencyTab ? ["Code", "Symbol", "Label", "Status", ""] : ["Code", "Label", "Status", ""]).map((h) => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#475569" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={isCurrencyTab ? 5 : 4} style={{ padding: "16px 10px", color: "var(--t2t-text-muted)", textAlign: "center" }}>No entries</td></tr>
            )}
            {rows.map((row) => (
              <tr key={row.lookup_id || row.id} style={{ borderBottom: "1px solid #F1F5F9" }}>
                <td style={{ padding: "7px 10px", fontWeight: 600 }}>{row.code}</td>
                {isCurrencyTab && (
                  <td style={{ padding: "7px 10px", fontSize: 14, fontWeight: 600 }}>{row.symbol || <span style={{ color: "#CBD5E1", fontStyle: "italic", fontSize: 11 }}>(none)</span>}</td>
                )}
                <td style={{ padding: "7px 10px" }}>{row.label}</td>
                <td style={{ padding: "7px 10px" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                    background: row.is_active ? "#D1FAE5" : "#FEE2E2",
                    color: row.is_active ? "#065F46" : "#991B1B" }}>
                    {row.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={{ padding: "7px 10px", textAlign: "right" }}>
                  <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => openEdit(row)} style={{ marginRight: 6 }}>✎ Edit</button>
                  <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ color: "#DC2626" }} onClick={() => remove(row)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <div className="t2t-overlay">
          <div className="t2t-modal" style={{ maxWidth: 460 }}>
            <div className="t2t-modal-head">
              <div className="t2t-modal-title">{modal.mode === "add" ? "Add Lookup Value" : "Edit Lookup Value"}</div>
              <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => setModal(null)}>✕</button>
            </div>
            <div className="t2t-modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Type</label>
                <select className="t2t-fc" value={form.lookup_type} onChange={(e) => setForm({ ...form, lookup_type: e.target.value })}>
                  {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              {/* BUG-17: Country picker for currencies (Add mode only — Edit doesn't need it) */}
              {isCurrency && modal.mode === "add" && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>
                    Country
                    <span style={{ fontWeight: 400, color: "#94A3B8", marginLeft: 6 }}>
                      (auto-fills code, label and symbol)
                    </span>
                  </label>
                  <select className="t2t-fc" value={form.country} onChange={(e) => onCountryPick(e.target.value)}>
                    <option value="">Select country…</option>
                    {COUNTRY_TO_CURRENCY.map(([country]) => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                    <option value="__other__">Other (enter manually)</option>
                  </select>
                </div>
              )}

              <div style={isCurrency ? { display: "grid", gridTemplateColumns: "1fr 110px", gap: 10 } : {}}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Code *</label>
                  <input className="t2t-fc" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder={codeHint} />
                </div>
                {isCurrency && (
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Symbol</label>
                    <input
                      className="t2t-fc"
                      value={form.symbol}
                      maxLength={10}
                      onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                      placeholder="₹ / $ / €"
                      style={{ fontSize: 14, fontWeight: 600, textAlign: "center" }}
                    />
                  </div>
                )}
              </div>

              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Label *</label>
                <input className="t2t-fc" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder={labelHint} />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" id="lu-active" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} />
                <label htmlFor="lu-active" style={{ fontSize: 12 }}>Active</label>
              </div>
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => setModal(null)}>Cancel</button>
              <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LookupAdmin;
